import React, {useCallback, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    Divider,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerOverlay,
    HStack,
    Text,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'

/**
 * Chakra only arms its scroll lock for the first modal in the stack (ModalFocusScope:
 * `enabled: index === 1`), so a drawer opened on top of another has every wheel/touchmove
 * cancelled by the lock underneath. Keeping those events off `document` restores native
 * scrolling, and only for the nested case — a lone drawer keeps Chakra's own handling.
 */
const useNestedScrollUnlock = (forwardedRef) => {
    const cleanupRef = useRef(null)

    useEffect(() => () => cleanupRef.current?.(), [])

    return useCallback(
        (node) => {
            cleanupRef.current?.()
            cleanupRef.current = null

            if (typeof forwardedRef === 'function') {
                forwardedRef(node)
            } else if (forwardedRef) {
                forwardedRef.current = node
            }

            if (!node) return

            const containers = node.ownerDocument.querySelectorAll(
                '.chakra-modal__content-container'
            )
            const isNested = Array.from(containers).some((container) => !container.contains(node))
            if (!isNested) return

            const stopPropagation = (event) => event.stopPropagation()
            node.addEventListener('wheel', stopPropagation, {passive: true})
            node.addEventListener('touchmove', stopPropagation, {passive: true})
            cleanupRef.current = () => {
                node.removeEventListener('wheel', stopPropagation)
                node.removeEventListener('touchmove', stopPropagation)
            }
        },
        [forwardedRef]
    )
}

/**
 * Reusable drawer shell: bottom sheet on small screens, right panel on lg (448px max width).
 * Same layout baseline as InfoCard / auth drawers.
 *
 * - With `headerTitle`: title + close row and divider under header.
 * - Without `headerTitle`: close-only row; put the main heading in `children` and pass its id as `ariaLabelledBy`.
 */
const StandardDrawer = ({
    isOpen,
    onClose,
    onOpen,
    ariaLabelledBy,
    headerTitle,
    headerTitleId,
    closeAriaLabel: closeAriaLabelProp,
    children,
    bodyProps,
    drawerProps,
    'data-testid': dataTestId
}) => {
    const {formatMessage} = useIntl()
    const {ref: forwardedBodyRef, ...restBodyProps} = bodyProps || {}
    const bodyRef = useNestedScrollUnlock(forwardedBodyRef)
    const placement = useBreakpointValue({base: 'bottom', lg: 'right'}, {fallback: 'right'})
    const closeAriaLabel =
        closeAriaLabelProp ??
        formatMessage({
            id: 'info_card.drawer.close.assistive_msg',
            defaultMessage: 'Close panel'
        })

    const showHeaderTitle = headerTitle != null

    return (
        <Drawer
            data-testid={dataTestId}
            isOpen={isOpen}
            onClose={onClose}
            onOpen={onOpen}
            placement={placement}
            size="xl"
            closeOnOverlayClick={true}
            isFullHeight
            {...drawerProps}
        >
            <DrawerOverlay />
            <DrawerContent
                aria-labelledby={ariaLabelledBy}
                height={{base: 'auto', lg: '100vh'}}
                maxH={{base: '90dvh', lg: '100vh'}}
                maxWidth={{base: '100%', lg: '448px'}}
                minHeight={{base: '300px'}}
                boxShadow="lg"
                borderRadius={0}
                m={0}
            >
                <HStack justifyContent="flex-end" p="10px 0 10px">
                    {showHeaderTitle && (
                        <Text
                            as="h3"
                            id={headerTitleId}
                            variant="drawerTitle"
                            marginRight="auto"
                            pl={4}
                        >
                            {headerTitle}
                        </Text>
                    )}
                    <DrawerCloseButton
                        position="relative"
                        top={0}
                        aria-label={closeAriaLabel}
                        data-testid={`${dataTestId}-close-button`}
                    />
                </HStack>
                {showHeaderTitle && <Divider mb={6} w="100%" borderColor="grey.500" />}
                <DrawerBody
                    bg="white"
                    paddingTop={showHeaderTitle ? 0 : 4}
                    paddingLeft={4}
                    paddingRight={4}
                    overflowY="auto"
                    overscrollBehavior="contain"
                    {...restBodyProps}
                    ref={bodyRef}
                >
                    {children}
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    )
}

StandardDrawer.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onOpen: PropTypes.func,
    ariaLabelledBy: PropTypes.string,
    headerTitle: PropTypes.node,
    headerTitleId: PropTypes.string,
    closeAriaLabel: PropTypes.string,
    children: PropTypes.node.isRequired,
    bodyProps: PropTypes.object,
    drawerProps: PropTypes.object,
    'data-testid': PropTypes.string
}

export default StandardDrawer
